// ShopFloorApi — every read and write the app makes, expressed as allow-listed
// calls through the BFF. Odoo field names here are the ones verified against
// the live instance; see the plan's mapping table.

import { ApiError, callKw, http, searchRead } from './http';

/**
 * The flowline-process topology (`mrp.flowline.operation`, `mrp.flowline.buffer`)
 * is not installed on production: Odoo answers 404 to the read, which the BFF
 * relays as an Odoo error. That is not a fault to retry or to show; it is a fact
 * about this instance, so the reads below answer empty and remember it for the
 * life of the page, and `lib/topology.ts` falls back to the buffer boards' own
 * table, which does exist. Without this the badge read polled a 404 every few
 * seconds from every phone on the floor.
 */
let topologyAbsent = false;
const absentHere = (e: unknown) => e instanceof ApiError && e.code === 'odoo_error';
import { measurePresets, widgetFor } from './checks';
import { dayStartUtc, localDateKey, localMidnightUtc, shiftDateKey } from '@/lib/pace';
import { defectDisplay, sortsLast } from '@/lib/defectSearch';
import { odooWallTime } from '@/lib/time';
import { edgesFromSteps } from '@/lib/topology';
import type {
  AlertMessageVM,
  AlertPayload,
  AlertVM,
  AndonState,
  AndonVM,
  BufferLineVM,
  CalendarVM,
  CrewShiftVM,
  QualityStageVM,
  DepartmentVM,
  DrawingFolderVM,
  FlowlineBufferVM,
  FlowlineOperationVM,
  RouteEdgeVM,
  StationStateVM,
  TodoVM,
  CheckState,
  CheckVM, FailedCheckVM,
  CompletedPodVM,
  DefectCategoryVM,
  FlowlineVM,
  JourneyStepVM,
  LossVM,
  OperatorVM,
  PodVM,
  RaisedAlertVM,
  SessionInfo,
  IswLang,
  IswRecord,
  IswRegister,
  TeamVM,
  WoLive,
  WoState,
  WorkcentreVM,
} from './types';

type M2O = [number, string] | false;
const id = (v: M2O): number | null => (Array.isArray(v) ? v[0] : null);
const label = (v: M2O): string => (Array.isArray(v) ? v[1] : '');

/**
 * What belongs in a station queue.
 *
 * `ready` and `progress` are here now and are ALWAYS shown, whatever state
 * their manufacturing order is in — on this instance every ready work order at
 * EOL QC belongs to a draft MO, so excluding drafts outright would empty the
 * station and make the app look broken.
 *
 * `pending` is Odoo's "waiting for another work order": still upstream, shown
 * deliberately so an operator can chase it, but only when the MO is real.
 * Draft orders are not released production and were most of the noise.
 *
 * Reads as: here-now OR (upstream AND not a draft).
 */
const QUEUE_DOMAIN: unknown[] = [
  '|',
  ['state', 'in', ['ready', 'progress']],
  '&',
  ['state', '=', 'pending'],
  ['production_id.state', '!=', 'draft'],
];

/** The most rows the BFF will read in one call (server/src/core/allowlist.ts). */
const MAX_READ_LIMIT = 200;

/** The queue is the production sequence — always ascending, never re-sorted. */
const QUEUE_ORDER = 'production_seq asc, sequence asc, id asc';

/**
 * Search ordering: what the operator most likely means comes first.
 *
 * A pod being worked on now is the common answer; one waiting to be closed is
 * the next. Then the settled ones — done before draft, because a finished pod
 * is a real pod and a draft is barely one yet — and cancelled last. `confirmed`
 * is open work Odoo has and this order does not name, so it sits with the open
 * end rather than the settled one.
 */
const MO_STATE_TIERS: string[][] = [['progress'], ['to_close'], ['confirmed'], ['done'], ['draft'], ['cancel']];

/** Which work order speaks for a pod: the one closest to being live. */
const WO_STATE_RANK: Record<WoState, number> = {
  progress: 0,
  ready: 1,
  pending: 2,
  waiting: 3,
  done: 4,
  cancel: 5,
};

const WO_FIELDS = [
  'id', 'name', 'state', 'workcenter_id', 'production_id', 'product_id', 'duration_expected',
  'qty_production', 'production_seq', 'flowline_id', 'check_ids', 'date_start', 'on_hold',
  // Odoo refuses button_start for an employee who is not permitted here.
  'allowed_employees', 'all_employees_allowed',
  // The work centre lets a failed check be signed off (Sign Off Anyway).
  'allow_failed_check_signoff',
];
const MO_FIELDS = [
  'id', 'name', 'related_project', 'related_project_nickname', 'pod_type_id',
  'product_attr_value_string', 'product_default_code', 'is_quarantine', 'on_hold',
  'has_open_alerts', 'is_rft', 'workorder_ids', 'state', 'production_seq',
];

interface WoRow {
  id: number; name: string; state: WoState; workcenter_id: M2O; production_id: M2O;
  product_id: M2O; duration_expected: number; qty_production: number; production_seq: number;
  flowline_id: M2O; check_ids: number[]; date_start: string | false;
  allowed_employees: number[]; all_employees_allowed: boolean;
  allow_failed_check_signoff: boolean;
}
interface MoRow {
  id: number; name: string; related_project: M2O; related_project_nickname: string | false;
  pod_type_id: M2O; product_attr_value_string: string | false; product_default_code: string | false;
  is_quarantine: boolean; on_hold: boolean; has_open_alerts: boolean; is_rft: boolean;
  workorder_ids: number[]; state: string; production_seq: number;
}

function fmtDuration(mins: number): string | null {
  if (!mins || mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

/** Attribute chips: "([LH] Left Hand, [CS1] Colour Scheme 1)" + handing code. */
export function specChips(attrString: string | false, defaultCode: string | false): string[] {
  const chips = new Set<string>();
  if (typeof defaultCode === 'string') {
    // e.g. "4836-A-LH.PRV.TUN" → LH, PRV, TUN
    const tail = defaultCode.split('-').slice(2).join('-');
    for (const part of tail.split('.')) {
      const token = part.trim();
      if (token && token.length <= 6) chips.add(token.toUpperCase());
    }
  }
  if (typeof attrString === 'string') {
    for (const m of attrString.matchAll(/\[([^\]]+)\]/g)) {
      const token = m[1]?.trim();
      if (token) chips.add(token.toUpperCase());
    }
  }
  return [...chips].slice(0, 6);
}

function podFrom(wo: WoRow, mo: MoRow | undefined): PodVM {
  const est = fmtDuration(wo.duration_expected);
  return {
    woId: wo.id,
    woName: wo.name,
    moId: id(wo.production_id) ?? 0,
    mo: label(wo.production_id) || mo?.name || '',
    proj:
      (typeof mo?.related_project_nickname === 'string' && mo.related_project_nickname) ||
      label(mo?.related_project ?? false) ||
      '',
    type: label(mo?.pod_type_id ?? false),
    spec: specChips(mo?.product_attr_value_string ?? false, mo?.product_default_code ?? false),
    productName: label(wo.product_id),
    state: wo.state,
    est,
    estMinutes: wo.duration_expected || 0,
    qty: wo.qty_production || 1,
    flowlineId: id(wo.flowline_id),
    flowlineName: label(wo.flowline_id) || null,
    productionSeq: wo.production_seq ?? 0,
    quarantine: !!mo?.is_quarantine,
    onHold: !!mo?.on_hold,
    hasOpenAlerts: !!mo?.has_open_alerts,
    checkCount: (wo.check_ids ?? []).length,
    moState: mo?.state ?? null,
    workcentreId: id(wo.workcenter_id) ?? 0,
    workcentreName: label(wo.workcenter_id),
    allEmployeesAllowed: !!wo.all_employees_allowed,
    allowedEmployeeIds: wo.allowed_employees ?? [],
    failedSignoffAllowed: !!wo.allow_failed_check_signoff,
  };
}

/** Strip HTML to text, for the read-gate length check. */
function textLength(html: string): number {
  return htmlToText(html).length;
}

/**
 * HTML → plain text, for chatter bodies and descriptions.
 *
 * Text, not sanitised HTML: nothing this app shows from a message body needs
 * markup, and rendering none is the one rule that cannot be got wrong.
 */
function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();
}

const ALERT_FIELDS = [
  'id', 'name', 'title', 'production_id', 'stage_id', 'priority', 'team_id', 'employee_id',
  'create_date', 'prevent_mo_signoff', 'workcenter_id', 'category_id', 'subcategory_id',
  'description', 'picture_ids',
  // The before / after buckets and the team rule that makes them mandatory
  // (interpod_quality). `picture_ids` is everything in neither bucket.
  'before_picture_ids', 'after_picture_ids', 'require_before_after_pictures',
];

/** Which picture bucket a photo goes into. 'other' is a plain attachment. */
export type PictureBucket = 'before' | 'after' | 'other';

/**
 * The andons an alert carries, read ONLY under the andon preview.
 *
 * Odoo rejects a read that names a field it does not have, and
 * `andon_alert_ids` exists only where interpod_andon is installed — so asking
 * for it on every quality read would blank every quality list on an instance
 * without the addon. The preview can only be on against an Odoo that has it,
 * which is why the one reader that wants the field (the quality sheet) asks
 * for it by that switch and nothing else does.
 */
const ALERT_FIELDS_WITH_ANDONS = [...ALERT_FIELDS, 'andon_alert_ids'];

interface AlertRow {
  id: number; name: string; title: string; production_id: M2O; stage_id: M2O;
  priority: string; team_id: M2O; employee_id: M2O; create_date: string; prevent_mo_signoff: boolean;
  workcenter_id: M2O; category_id: M2O; subcategory_id: M2O; description: string | false;
  picture_ids: number[];
  before_picture_ids: number[];
  after_picture_ids: number[];
  require_before_after_pictures: boolean;
  // Absent on an instance whose addon predates the andon link.
  andon_alert_ids?: number[];
}

function alertFrom(r: AlertRow, now: number, employeeId: number | null): AlertVM {
  const category = [label(r.category_id), label(r.subcategory_id)].filter(Boolean).map(defectDisplay).join(' › ');
  return {
    id: r.id,
    qa: r.name,
    // Odoo builds a categorised alert's title from the defect's names.
    title: defectDisplay(r.title ?? ''),
    moId: id(r.production_id),
    mo: label(r.production_id) || null,
    stageId: id(r.stage_id),
    stage: label(r.stage_id),
    stageDone: false,
    priority: r.priority,
    teamId: id(r.team_id) ?? 0,
    team: label(r.team_id),
    employeeId: id(r.employee_id),
    employeeName: label(r.employee_id),
    workcentreId: id(r.workcenter_id),
    workcentreName: label(r.workcenter_id),
    categoryId: id(r.category_id),
    subcategoryId: id(r.subcategory_id),
    category,
    description: htmlToText(typeof r.description === 'string' ? r.description : ''),
    pictureIds: r.picture_ids ?? [],
    beforePictureIds: r.before_picture_ids ?? [],
    afterPictureIds: r.after_picture_ids ?? [],
    requirePictures: !!r.require_before_after_pictures,
    andonAlertIds: r.andon_alert_ids ?? [],
    createdAt: r.create_date,
    age: ageFrom(r.create_date, now),
    mine: employeeId != null && id(r.employee_id) === employeeId,
    preventMoSignoff: r.prevent_mo_signoff,
  };
}

/** Where an attachment's bytes are served from — only ever a quality alert's photo. */
export const attachmentImageUrl = (attachmentId: number): string =>
  `/api/image?model=ir.attachment&field=datas&id=${attachmentId}`;

function ageFrom(iso: string, now: number): string {
  const then = Date.parse(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((now - then) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

/**
 * Where an employee's photo lives.
 *
 * The roster hands one out per row, and the signed-in operator arrives from the
 * session with an id and a name and nothing else — so their own tile builds the
 * same URL from the same id rather than going without a face. Which employee
 * model this Odoo will actually answer on is the BFF's problem, not this one's:
 * see the image route, which falls back to the public twin itself.
 */
export const employeeAvatarUrl = (employeeId: number): string =>
  `/api/image?model=hr.employee&field=avatar_128&id=${employeeId}`;

/**
 * A library render URL → this app's own path for it.
 *
 * The library authors its URLs against its own origin ("/cards/ISW-029 … —
 * mobile.html?v=2.5"). They are rebased here rather than used as given,
 * because the card must be fetched by the BFF — which holds the service token
 * — and must arrive on THIS origin for the viewer to frame and measure it.
 *
 * Anything that is not one of the library's own /cards/ paths returns null.
 * The URL arrives over the network, and a viewer that will frame whatever it
 * is handed is a viewer that can be pointed anywhere.
 */
export const iswCardUrl = (libraryUrl: string): string | null => {
  const m = /^\/cards\/([^/?#]+)(\?v=[^#]*)?$/.exec(libraryUrl);
  return m ? `/isw/cards/${m[1]}${m[2] ?? ''}` : null;
};

/** What `mrp.production.cdocs_data` answers — only the parts this app reads. */
interface CdocsData {
  error?: string | false;
  folder?: { id: string; name: string; path: string; level: string; is_root: boolean; root_id: string } | false;
  siblings?: Array<{ id: string; name: string }>;
  entries?: Array<
    | { id: string; name: string; kind: 'folder'; count?: number }
    | {
        id: string; name: string; kind: 'pdf'; size?: number; revision?: string;
        revision_status?: string; approved?: boolean; url?: string;
      }
  >;
}

/**
 * Odoo's URL for a drawing → this app's.
 *
 * Odoo writes `/controlled_docs/file/<item>/<revision token>?production_id=…`,
 * a path the device cannot reach — the BFF holds the only Odoo session. The
 * item and the token are lifted out of it, and nothing else is: the pod comes
 * from the pod the sheet is open on, not from the string Odoo sent back. A URL
 * of any other shape gives null rather than a guess.
 */
export const drawingFileUrl = (odooUrl: string | undefined, moId: number): string | null => {
  const m = /^\/controlled_docs\/file\/([A-Za-z0-9!]{8,128})\/([0-9a-f]{12})(?:\?|$)/.exec(odooUrl ?? '');
  return m ? `/drawings/file/${m[1]}/${m[2]}?mo=${moId}` : null;
};

function drawingFolderFrom(raw: CdocsData, moId: number): DrawingFolderVM {
  const folder = raw.folder || null;
  const entries = raw.entries ?? [];
  return {
    folder: folder
      ? {
          id: folder.id,
          name: folder.name,
          path: folder.path,
          level: folder.level === 'type' ? 'type' : 'project',
          isRoot: folder.is_root,
          rootId: folder.root_id,
        }
      : null,
    unavailable: folder ? null : typeof raw.error === 'string' && raw.error ? raw.error : null,
    siblings: raw.siblings ?? [],
    folders: entries.flatMap((e) => (e.kind === 'folder' ? [{ id: e.id, name: e.name, count: e.count ?? 0 }] : [])),
    drawings: entries.flatMap((e) =>
      e.kind === 'pdf'
        ? [
            {
              id: e.id,
              name: e.name,
              size: e.size ?? 0,
              revision: e.revision ?? '',
              status: e.revision_status ?? '',
              approved: !!e.approved,
              url: drawingFileUrl(e.url, moId),
            },
          ]
        : [],
    ),
  };
}

export const api = {
  // ── session ──────────────────────────────────────────────────────────────
  sessionInfo: () => http.get<SessionInfo>('/api/session/info'),
  loginCredentials: (login: string, password: string) =>
    http.post<SessionInfo | { totpRequired: true }>('/api/session/login', { login, password }),
  submitTotp: (code: string, remember?: boolean) =>
    http.post<SessionInfo>('/api/session/totp', { code, remember }),
  connectKiosk: () => http.post<SessionInfo>('/api/session/kiosk', {}),
  pinConfirm: (employeeId: number, pin: string) =>
    http.post<{ ok: true; operator: OperatorVM }>('/api/session/pin', { employeeId, pin }),
  logout: (opts: { full?: boolean; stopWork?: boolean } = {}) =>
    http.post<{ ok: true; sessionKept: boolean }>('/api/session/logout', opts),

  // ── the standard-work library ────────────────────────────────────────────
  //
  // Both go through this app's BFF rather than at the library directly. The
  // credential that opens the library is a service token held server-side, and
  // a card has to arrive on THIS origin for the viewer to frame it at all —
  // see server/src/core/isw.ts for the full reasoning.
  iswRegister: () => http.get<IswRegister>('/api/isw/register'),
  iswRecord: (id: string, lang: IswLang) =>
    http.get<IswRecord>(`/api/isw/records/${encodeURIComponent(id)}?lang=${lang}`),

  // ── a pod's controlled drawings ──────────────────────────────────────────
  //
  // Odoo resolves the folder — the pod type's when one is bound, the project's
  // otherwise — and reads every revision fresh from SharePoint, so this is one
  // call and there is nothing to cache on this side. `folderId` drills into a
  // subfolder, and Odoo refuses one that is not under this pod's own folders.
  async drawings(moId: number, folderId: string | null): Promise<DrawingFolderVM> {
    const raw = await callKw<CdocsData>(
      'mrp.production',
      'cdocs_data',
      [[moId]],
      folderId ? { item_id: folderId } : {},
    );
    return drawingFolderFrom(raw ?? {}, moId);
  },

  // ── reference data ───────────────────────────────────────────────────────
  async flowlines(): Promise<FlowlineVM[]> {
    const rows = await searchRead<{ id: number; name: string; prefix: string | false; sequence: number }>(
      'mrp.flowline',
      [['active', '=', true]],
      ['id', 'name', 'prefix', 'sequence'],
      { order: 'sequence', limit: 50 },
    );
    return (
      rows
        // Staging carries demo rows with no prefix; a flowline the operator can
        // pick must have one, since it is how the line is labelled everywhere.
        .filter((r) => typeof r.prefix === 'string' && r.prefix.length > 0)
        .map((r) => ({ id: r.id, name: r.name, prefix: r.prefix as string, sequence: r.sequence }))
    );
  },

  async workcentres(): Promise<WorkcentreVM[]> {
    const rows = await searchRead<{
      id: number; name: string; code: string | false; sequence: number;
      is_eol: boolean; is_flowline_workcenter: boolean; working_state: string;
      department_id: M2O; resource_calendar_id: M2O; employee_ids: number[];
    }>(
      'mrp.workcenter',
      [],
      [
        'id', 'name', 'code', 'sequence', 'is_eol', 'is_flowline_workcenter', 'working_state',
        // The leader's filter chips, and the calendar every pace sum runs against.
        'department_id', 'resource_calendar_id',
        // The roster, so the station picker can lead with the operator's own.
        'employee_ids',
      ],
      { order: 'sequence,id', limit: 200 },
    );
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      code: typeof r.code === 'string' ? r.code : null,
      sequence: r.sequence,
      isEol: r.is_eol,
      isFlowline: r.is_flowline_workcenter,
      blocked: r.working_state === 'blocked',
      departmentId: id(r.department_id),
      departmentName: label(r.department_id),
      calendarId: id(r.resource_calendar_id),
      employeeIds: r.employee_ids ?? [],
    }));
  },

  /** Queue depth per work centre, for the station picker. */
  async waitingCounts(flowlineId?: number | null): Promise<Record<number, number>> {
    const domain: unknown[] = [['on_hold', '=', false], ...QUEUE_DOMAIN];
    if (flowlineId) domain.push(['flowline_id', '=', flowlineId]);
    const groups = await callKw<Array<{ workcenter_id: M2O; __count?: number; workcenter_id_count?: number }>>(
      'mrp.workorder',
      'read_group',
      [],
      { domain, fields: ['id:count'], groupby: ['workcenter_id'], limit: 200 },
    );
    const out: Record<number, number> = {};
    for (const g of groups) {
      const wcId = id(g.workcenter_id);
      if (wcId != null) out[wcId] = g.workcenter_id_count ?? g.__count ?? 0;
    }
    return out;
  },

  async teams(): Promise<TeamVM[]> {
    const rows = await searchRead<{
      id: number; name: string; use_alert_categories: boolean;
      prevent_mo_signoff: boolean; deadline_days: number; approver_ids: number[];
      require_before_after_pictures: boolean;
    }>(
      'quality.alert.team',
      [],
      ['id', 'name', 'use_alert_categories', 'prevent_mo_signoff', 'deadline_days', 'approver_ids', 'require_before_after_pictures'],
      { limit: 50 },
    );
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      useCategories: r.use_alert_categories,
      preventMoSignoff: r.prevent_mo_signoff,
      deadlineDays: r.deadline_days,
      approverUids: r.approver_ids ?? [],
      requirePictures: !!r.require_before_after_pictures,
    }));
  },

  async lossReasons(): Promise<LossVM[]> {
    // manual=true AND loss_type set — the instance carries two junk rows, one
    // with a null loss_type that would break any OEE bucketing.
    const rows = await searchRead<{ id: number; name: string; loss_type: string | false; manual: boolean }>(
      'mrp.workcenter.productivity.loss',
      [['manual', '=', true]],
      ['id', 'name', 'loss_type', 'manual'],
      { limit: 50 },
    );
    const DOTS = ['#c0791f', '#d64550', '#2f6bff', '#1f9d57', '#a855f7'];
    return rows
      .filter((r) => typeof r.loss_type === 'string' && r.loss_type)
      .map((r, i) => ({ id: r.id, name: r.name, lossType: r.loss_type as string, dot: DOTS[i % DOTS.length]! }));
  },

  /**
   * Every active defect, paged. The taxonomy outgrew one read: production
   * holds 329 rows against the BFF's 200-row page, and a single page in
   * Odoo's own complete_name order silently dropped everything from about
   * "Shower screens" on — Tiling, Wall linings, Waterproofing could not be
   * found at all. Paged on id so a row cannot fall between two pages; the
   * order is put back here: by name, with the rows the office prefixed `[x]`
   * last, as Odoo lists them — and then the prefix comes off, since it was only
   * ever there for that order (lib/defectSearch `defectDisplay`). A row with no
   * name (Odoo has a few, saved blank) is nothing an operator could pick and
   * is left out.
   */
  async defectCategories(): Promise<DefectCategoryVM[]> {
    type Row = { id: number; name: string | false; parent_id: M2O; complete_name: string };
    const rows: Row[] = [];
    for (let page = 0; page < 10; page++) {
      const batch = await searchRead<Row>(
        'quality.alert.category',
        [['active', '=', true]],
        ['id', 'name', 'parent_id', 'complete_name'],
        { limit: MAX_READ_LIMIT, offset: page * MAX_READ_LIMIT, order: 'id' },
      );
      rows.push(...batch);
      if (batch.length < MAX_READ_LIMIT) break;
    }
    return rows
      .filter((r): r is Row & { name: string } => typeof r.name === 'string' && r.name.trim() !== '')
      .sort(
        (a, b) =>
          Number(sortsLast(a.name)) - Number(sortsLast(b.name)) ||
          defectDisplay(a.name).localeCompare(defectDisplay(b.name)),
      )
      .map((r) => ({
        id: r.id,
        name: defectDisplay(r.name),
        parentId: id(r.parent_id),
        completeName: defectDisplay(r.complete_name),
      }));
  },

  /** Employee roster for the kiosk picker. Never includes PINs. */
  async roster(): Promise<OperatorVM[]> {
    for (const model of ['hr.employee', 'hr.employee.public'] as const) {
      try {
        const rows = await searchRead<{ id: number; name: string; job_title: string | false; workcenter_ids: number[] }>(
          model,
          [['active', '=', true]],
          ['id', 'name', 'job_title', 'workcenter_ids'],
          { order: 'name', limit: 200 },
        );
        return rows.map((r) => ({
          id: r.id,
          name: r.name,
          jobTitle: typeof r.job_title === 'string' ? r.job_title : undefined,
          workcentreIds: r.workcenter_ids ?? [],
          avatarUrl: employeeAvatarUrl(r.id),
        }));
      } catch {
        // Fall through to the public model if the service account lacks access.
      }
    }
    return [];
  },

  // ── queue ────────────────────────────────────────────────────────────────
  async queue(workcentreId: number, flowlineId: number | null, offset = 0, limit = 40): Promise<PodVM[]> {
    const domain: unknown[] = [
      ['workcenter_id', '=', workcentreId],
      // On hold means "taken out of the production sequence" — those pods are
      // not work anyone at this station should pick up.
      ['on_hold', '=', false],
      ...QUEUE_DOMAIN,
    ];
    if (flowlineId) domain.push(['flowline_id', '=', flowlineId]);
    const wos = await searchRead<WoRow>('mrp.workorder', domain, WO_FIELDS, {
      order: QUEUE_ORDER,
      limit,
      offset,
    });
    return this.hydrate(wos);
  },

  /** Batch-read the MOs for a set of work orders and build pod view models. */
  async hydrate(wos: WoRow[]): Promise<PodVM[]> {
    const moIds = [...new Set(wos.map((w) => id(w.production_id)).filter((v): v is number => v != null))];
    const mos = moIds.length
      ? await callKw<MoRow[]>('mrp.production', 'read', [moIds], { fields: MO_FIELDS })
      : [];
    const byId = new Map(mos.map((m) => [m.id, m]));
    return wos.map((wo) => podFrom(wo, byId.get(id(wo.production_id) ?? -1)));
  },

  /**
   * Resolve a scanned label to a pod waiting at this station.
   *
   * A label may carry the whole work order barcode (`MO/38716/1105789`) or just
   * the pod's MO number, so both are matched — exactly, never with a wildcard,
   * because a scan is an exact reading and `ilike` would let a stray `%` in the
   * payload match an arbitrary pod.
   *
   * Returns null when the code is genuinely not at this station. Transport and
   * permission failures throw: "not here" and "could not ask" must not look the
   * same to an operator standing in front of the pod.
   */
  async findByCode(code: string, workcentreId: number): Promise<PodVM | null> {
    const wos = await searchRead<WoRow>(
      'mrp.workorder',
      [
        ['workcenter_id', '=', workcentreId],
        ['on_hold', '=', false],
        ...QUEUE_DOMAIN,
        '|',
        ['barcode', '=', code],
        ['production_id.name', '=', code],
      ],
      WO_FIELDS,
      { order: QUEUE_ORDER, limit: 1 },
    );
    const pods = await this.hydrate(wos);
    return pods[0] ?? null;
  },

  /**
   * The pod a work order belongs to, wherever it is.
   *
   * Only for restoring a `/job/<woId>` URL: the queue answers this for every
   * pod an operator can reach by tapping, and it is the pod they had open when
   * the tablet reloaded that the queue may no longer carry — finished since,
   * put on hold since, or at a station this one is no longer pointed at.
   */
  async podByWo(woId: number): Promise<PodVM | null> {
    const wos = await searchRead<WoRow>('mrp.workorder', [['id', '=', woId]], WO_FIELDS, { limit: 1 });
    const pods = await this.hydrate(wos);
    return pods[0] ?? null;
  },

  /**
   * The pod finder: search every station, not just this one, because the reason
   * an operator reaches for it is usually that the pod is *not* where they
   * expected. Matches the things actually printed on a pod or its paperwork.
   *
   * Pods in *any* state are searchable. Someone typing a pod number is holding
   * that pod; answering "no such pod" because it is finished, cancelled or
   * still a draft is never the truth they need. State decides the order
   * instead of membership — see MO_STATE_TIERS.
   */
  async searchPods(query: string, workcentreId: number | null, limit = 25): Promise<PodVM[]> {
    const q = query.trim();
    if (q.length < 2) return [];

    const matches: unknown[] = [
      '|', '|', '|',
      ['name', 'ilike', q],
      ['product_default_code', 'ilike', q],
      ['related_project', 'ilike', q],
      ['related_project_nickname', 'ilike', q],
    ];

    /**
     * Candidates come from the pods themselves, not their work orders.
     *
     * A pod carries thirty-odd work orders and the BFF reads at most
     * MAX_READ_LIMIT rows in one call. Searching work orders with no state
     * filter would spend that whole budget on the first handful of pods and
     * silently drop the rest of a project.
     *
     * Asking one state tier at a time is what makes the ordering real rather
     * than cosmetic: a project can have a thousand finished pods, and ranking
     * one page of those locally would never surface the one in progress.
     * Filling from the top means the search usually stops after the first tier.
     */
    const wanted: MoRow[] = [];
    for (const tier of MO_STATE_TIERS) {
      if (wanted.length >= limit) break;
      wanted.push(
        ...(await searchRead<MoRow>('mrp.production', [...matches, ['state', 'in', tier]], MO_FIELDS, {
          order: 'production_seq asc, id asc',
          limit: limit - wanted.length,
        })),
      );
    }
    if (wanted.length === 0) return [];

    // One work order stands for each pod: the one this operator would open.
    const woFor = new Map<number, WoRow>();
    const claim = (rows: WoRow[]) => {
      for (const wo of rows) {
        const moId = id(wo.production_id);
        if (moId == null) continue;
        const held = woFor.get(moId);
        if (!held || WO_STATE_RANK[wo.state] < WO_STATE_RANK[held.state]) woFor.set(moId, wo);
      }
    };
    const unclaimed = () => wanted.filter((m) => !woFor.has(m.id)).map((m) => m.id);

    // Answer from this station first.
    //
    // An operator searching is standing at a work centre and asking "what is
    // this pod's state *here*" — not "where is it now". Showing the pod's
    // active work order sent them to whatever station currently holds it,
    // which is a different question and usually the wrong place to be taken.
    if (workcentreId != null) {
      claim(
        await searchRead<WoRow>(
          'mrp.workorder',
          [['production_id', 'in', wanted.map((m) => m.id)], ['workcenter_id', '=', workcentreId]],
          WO_FIELDS,
          { order: QUEUE_ORDER, limit: MAX_READ_LIMIT },
        ),
      );
    }

    // A pod that never routes through here, or has already passed, still needs
    // to be findable — show it where it is live, and the row will say so.
    let rest = unclaimed();
    if (rest.length) {
      claim(
        await searchRead<WoRow>(
          'mrp.workorder',
          [['production_id', 'in', rest], ['state', 'in', ['progress', 'ready']]],
          WO_FIELDS,
          { order: QUEUE_ORDER, limit: MAX_READ_LIMIT },
        ),
      );
    }

    // What is left is settled or not started: nothing is live anywhere, so take
    // the last step of the route — where the pod ended up, or is headed.
    rest = unclaimed();
    if (rest.length) {
      const tails = wanted
        .filter((m) => rest.includes(m.id))
        .map((m) => m.workorder_ids[m.workorder_ids.length - 1])
        .filter((v): v is number => typeof v === 'number');
      if (tails.length) {
        claim(await callKw<WoRow[]>('mrp.workorder', 'read', [tails], { fields: WO_FIELDS }));
      }
    }

    // Built from the MOs already in hand — they are the pods, and re-reading
    // them through hydrate would only ask Odoo the same question twice.
    return wanted
      .map((mo) => {
        const wo = woFor.get(mo.id);
        return wo ? podFrom(wo, mo) : null;
      })
      .filter((p): p is PodVM => p !== null);
  },

  // ── checks ───────────────────────────────────────────────────────────────
  /**
   * Every failed check the pod still carries, on any of its work orders —
   * what EOL QC is shown before it signs the pod off the line with them.
   */
  async podFailedChecks(moId: number): Promise<FailedCheckVM[]> {
    const rows = await searchRead<{
      id: number; title: string | false; name: string | false; workorder_id: M2O; workcenter_id: M2O;
    }>('quality.check', [['production_id', '=', moId], ['quality_state', '=', 'fail']], [
      'id', 'title', 'name', 'workorder_id', 'workcenter_id',
    ], { order: 'workorder_id,sequence,id', limit: 100 });
    return rows.map((r) => ({
      id: r.id,
      title: (typeof r.title === 'string' && r.title) || (typeof r.name === 'string' && r.name) || '',
      woId: id(r.workorder_id),
      workcentreName: label(r.workcenter_id),
    }));
  },

  async checks(woId: number): Promise<CheckVM[]> {
    // Read check_ids directly, sorted by point sequence: the next_check_id
    // chain is known to fracture, which would hide pending checks.
    const rows = await searchRead<{
      id: number; title: string | false; name: string | false; test_type_id: M2O;
      quality_state: CheckState; point_id: M2O; measure: number; norm_unit: string | false;
      tolerance_min: number; tolerance_max: number; note: string | false;
      failure_message: string | false; selected_option_id: M2O; possible_option_ids: number[];
      registered_value: string | false; images: number[]; sequence: number;
      mould_id: M2O; allowed_mould_ids: number[];
    }>('quality.check', [['workorder_id', '=', woId]], [
      'id', 'title', 'name', 'test_type_id', 'quality_state', 'point_id', 'measure', 'norm_unit',
      'tolerance_min', 'tolerance_max', 'note', 'failure_message', 'selected_option_id',
      'possible_option_ids', 'registered_value', 'images', 'sequence', 'mould_id', 'allowed_mould_ids',
    ], { order: 'sequence,id', limit: 100 });

    // The x2many fields above arrive as bare ids. Resolve the names in one
    // read per model for the whole list, so a pod with several Select Option
    // checks costs two round trips, not one per check. Order is preserved from
    // the check's own field, which is the order the point was configured in.
    const optionIds = [...new Set(rows.flatMap((r) => r.possible_option_ids ?? []))];
    const mouldIds = [...new Set(rows.flatMap((r) => r.allowed_mould_ids ?? []))];
    const [optionRows, mouldRows] = await Promise.all([
      optionIds.length
        ? callKw<Array<{ id: number; name: string; active: boolean }>>(
            'quality.check.selection.option',
            'read',
            [optionIds],
            { fields: ['id', 'name', 'active'] },
          )
        : Promise.resolve([]),
      mouldIds.length
        ? callKw<Array<{ id: number; display_name: string }>>('mrp.mould', 'read', [mouldIds], {
            fields: ['id', 'display_name'],
          })
        : Promise.resolve([]),
    ]);
    // An archived option is still in the point's set, but is not something an
    // operator may pick now.
    const optionName = new Map(optionRows.filter((o) => o.active).map((o) => [o.id, o.name]));
    const mouldName = new Map(mouldRows.map((m) => [m.id, m.display_name]));
    const named = (ids: number[] | undefined, names: Map<number, string>) =>
      (ids ?? []).flatMap((oid) => {
        const name = names.get(oid);
        return name ? [{ id: oid, name }] : [];
      });

    return rows.map((r) => {
      const { widget, allowNa } = widgetFor(id(r.test_type_id));
      const note = typeof r.note === 'string' ? r.note : '';
      const max = r.tolerance_max ?? null;
      const vm: CheckVM = {
        id: r.id,
        widget,
        testTypeId: id(r.test_type_id) ?? 0,
        title: (typeof r.title === 'string' && r.title) || label(r.point_id) || 'Check',
        hint: '',
        state: r.quality_state,
        instructionsHtml: note,
        instructionsLength: textLength(note),
        sequence: r.sequence ?? 0,
        allowNa,
      };
      if (widget === 'measure') {
        vm.measure = {
          value: typeof r.measure === 'number' && r.measure !== 0 ? r.measure : null,
          unit: typeof r.norm_unit === 'string' ? r.norm_unit : '',
          min: r.tolerance_min ?? null,
          max,
          presets: measurePresets(max),
        };
      }
      if (widget === 'selection') {
        vm.options = named(r.possible_option_ids, optionName);
        vm.selectedOptionId = id(r.selected_option_id);
      }
      if (widget === 'mould') {
        vm.moulds = named(r.allowed_mould_ids, mouldName);
        vm.mouldId = id(r.mould_id);
      }
      if (widget === 'value') vm.registeredValue = typeof r.registered_value === 'string' ? r.registered_value : '';
      if (widget === 'photo') vm.hasPhoto = (r.images?.length ?? 0) > 0;
      if (typeof r.failure_message === 'string' && r.failure_message) vm.failureMessage = r.failure_message;
      return vm;
    });
  },

  // ── work order lifecycle ─────────────────────────────────────────────────
  startWo: (woId: number) => callKw<unknown>('mrp.workorder', 'button_start', [[woId]]),
  /** Native pause is button_pending — button_pause does not exist in Odoo 18. */
  pauseWo: (woId: number) => callKw<unknown>('mrp.workorder', 'button_pending', [[woId]]),
  finishWo: (woId: number) => callKw<unknown>('mrp.workorder', 'button_finish', [[woId]]),

  /**
   * Live timing from the work order's productivity lines. One round trip: the
   * work order's own state already came with the queue, and this runs on the
   * path between tapping Start and seeing the job.
   */
  async woLive(woId: number): Promise<WoLive> {
    const lines = await searchRead<{ id: number; date_start: string; date_end: string | false; duration: number }>(
      'mrp.workcenter.productivity',
      [['workorder_id', '=', woId]],
      ['id', 'date_start', 'date_end', 'duration'],
      { limit: 100 },
    );
    let openLineId: number | null = null;
    let startedAt: number | null = null;
    let priorSec = 0;
    for (const l of lines) {
      if (!l.date_end) {
        openLineId = l.id;
        startedAt = Date.parse(l.date_start.replace(' ', 'T') + 'Z');
      } else {
        priorSec += Math.round((l.duration ?? 0) * 60);
      }
    }
    return {
      woId,
      startedAt: startedAt != null && Number.isNaN(startedAt) ? null : startedAt,
      priorDurationSec: priorSec,
      serverNow: Date.now(),
      openLineId,
    };
  },

  /** Pause reason / attestation notes go to MO chatter (WOs have none). */
  postMoNote: (moId: number, body: string) =>
    callKw<unknown>('mrp.production', 'message_post', [[moId]], { body }),

  // ── check actions ────────────────────────────────────────────────────────
  passCheck: (checkId: number) => callKw<unknown>('quality.check', 'do_pass_immediate', [[checkId]]),
  failCheck: (checkId: number) => callKw<unknown>('quality.check', 'do_fail_immediate', [[checkId]]),
  naCheck: (checkId: number) => callKw<unknown>('quality.check', 'do_na_immediate', [[checkId]]),
  undoCheck: (checkId: number) => callKw<unknown>('quality.check', 'do_todo_immediate', [[checkId]]),
  async measureCheck(checkId: number, value: number): Promise<void> {
    // Write the measure, then let Odoo evaluate it against the point tolerance.
    await callKw('quality.check', 'write', [[checkId], { measure: value }]);
    await callKw('quality.check', 'do_measure', [[checkId]]);
  },
  async photoCheck(checkId: number, base64: string): Promise<void> {
    await callKw('quality.check', 'write', [[checkId], { picture: base64 }]);
    await callKw('quality.check', 'do_pass_immediate', [[checkId]]);
  },
  async selectOption(checkId: number, optionId: number): Promise<void> {
    await callKw('quality.check', 'write', [[checkId], { selected_option_id: optionId }]);
    await callKw('quality.check', 'do_pass_immediate', [[checkId]]);
  },
  /** Register Mould: Odoo's do_pass_immediate guards the pick and stamps it on the MO. */
  async registerMould(checkId: number, mouldId: number): Promise<void> {
    await callKw('quality.check', 'write', [[checkId], { mould_id: mouldId }]);
    await callKw('quality.check', 'do_pass_immediate', [[checkId]]);
  },
  async registerValue(checkId: number, text: string): Promise<void> {
    await callKw('quality.check', 'write', [[checkId], { registered_value: text }]);
    await callKw('quality.check', 'do_pass_immediate', [[checkId]]);
  },

  // ── alerts ───────────────────────────────────────────────────────────────
  async raiseAlert(p: AlertPayload): Promise<RaisedAlertVM> {
    const context: Record<string, number> = {};
    if (p.productionId) context['default_production_id'] = p.productionId;
    if (p.workorderId) context['default_workorder_id'] = p.workorderId;
    const vals: Record<string, unknown> = { title: p.title, team_id: p.teamId };
    if (p.description) vals['description'] = p.description;
    if (p.priority) vals['priority'] = p.priority;
    if (p.categoryId) vals['category_id'] = p.categoryId;
    if (p.subcategoryId) vals['subcategory_id'] = p.subcategoryId;
    if (p.productionId) vals['production_id'] = p.productionId;
    if (p.workcentreId) vals['workcenter_id'] = p.workcentreId;

    const alertId = await callKw<number>('quality.alert', 'create', [vals], { context });
    // Read back the REAL QA number — never fabricate one.
    const [row] = await callKw<Array<{ id: number; name: string; title: string; team_id: M2O; stage_id: M2O; date_deadline: string | false }>>(
      'quality.alert',
      'read',
      [[alertId]],
      { fields: ['id', 'name', 'title', 'team_id', 'stage_id', 'date_deadline'] },
    );
    return {
      id: alertId,
      qa: row?.name ?? '',
      title: defectDisplay(row?.title ?? p.title),
      team: label(row?.team_id ?? false),
      stage: label(row?.stage_id ?? false),
      deadline: typeof row?.date_deadline === 'string' ? row.date_deadline : null,
    };
  },

  /**
   * Attach a photo to a quality alert, into one of its picture buckets.
   *
   * With no alert named, the BFF pins it to the one this operator just created
   * — the raise flow. The quality sheet names the alert it is open on.
   *
   * The attachment is created first, as a plain chatter attachment; a 'before'
   * or 'after' bucket is then a second write linking it in (`[[4, id]]`, the
   * only bucket command the BFF lets through). The link needs the alert id
   * explicitly — the BFF's "just created" pin covers the attachment's res_id
   * only, not a write on the alert — so the raise flow passes the id it got
   * back from `raiseAlert`. Odoo refuses to link an attachment that belongs
   * to another record. Resolves to the attachment id.
   */
  async attachAlertPhoto(name: string, base64: string, alertId?: number, bucket: PictureBucket = 'other'): Promise<number> {
    const attachmentId = await callKw<number>('ir.attachment', 'create', [
      { name, datas: base64, ...(alertId != null ? { res_id: alertId } : {}) },
    ]);
    if (bucket !== 'other') {
      if (alertId == null) throw new Error('attachAlertPhoto: a bucket needs the alert id');
      await callKw<unknown>('quality.alert', 'write', [[alertId], { [`${bucket}_picture_ids`]: [[4, attachmentId]] }]);
    }
    return attachmentId;
  },

  /**
   * Move one of an alert's pictures between the before / after / other
   * buckets. 'other' takes it out of both; the server owns the unlink.
   */
  movePicture: (alertId: number, attachmentId: number, bucket: PictureBucket) =>
    callKw<unknown>('quality.alert', 'move_picture', [[alertId], attachmentId, bucket]),

  /**
   * Open quality alerts, by what they are about.
   *
   * `mine` is the ones this operator raised; `workcentreId` the ones stamped
   * with a station; `blocking` narrows to the ones holding a pod's sign-off;
   * `excludeTeamIds` leaves out teams whose alerts are not about the floor
   * (see SF_QA_EXCLUDED_TEAM_IDS). `ids` reads named alerts whatever their
   * stage, for a sheet opened on one that may since have closed.
   */
  async alerts(scope: {
    moId?: number;
    mine?: boolean;
    employeeId?: number;
    /** Alerts whose Responsible is this Odoo user — assigned to them. */
    userId?: number;
    workcentreId?: number;
    blocking?: boolean;
    excludeTeamIds?: number[];
    ids?: number[];
    limit?: number;
    /** Read `andon_alert_ids` as well. Only ever true under the andon preview. */
    withAndons?: boolean;
  }): Promise<AlertVM[]> {
    const domain: unknown[] = scope.ids ? [['id', 'in', scope.ids]] : [['stage_id.done', '=', false]];
    if (scope.moId) domain.push(['production_id', '=', scope.moId]);
    if (scope.mine && scope.employeeId) domain.push(['employee_id', '=', scope.employeeId]);
    if (scope.userId) domain.push(['user_id', '=', scope.userId]);
    if (scope.workcentreId) domain.push(['workcenter_id', '=', scope.workcentreId]);
    if (scope.blocking) domain.push(['prevent_mo_signoff', '=', true]);
    if (scope.excludeTeamIds?.length) domain.push(['team_id', 'not in', scope.excludeTeamIds]);
    const rows = await searchRead<AlertRow>('quality.alert', domain, scope.withAndons ? ALERT_FIELDS_WITH_ANDONS : ALERT_FIELDS, {
      order: 'create_date desc',
      limit: scope.limit ?? 50,
    });
    const now = Date.now();
    return rows.map((r) => alertFrom(r, now, scope.employeeId ?? null));
  },

  /** One quality alert, whatever stage it is in. */
  async alertById(alertId: number, withAndons = false): Promise<AlertVM | null> {
    const rows = await api.alerts({ ids: [alertId], limit: 1, withAndons });
    return rows[0] ?? null;
  },

  /**
   * The stages a quality alert may move between, for one team.
   *
   * Odoo scopes a stage to teams through `team_ids`; a stage with none is
   * shared. This is the same domain the alert form's stage bar uses.
   */
  async qualityStages(teamId: number): Promise<QualityStageVM[]> {
    const rows = await searchRead<{ id: number; name: string; sequence: number; done: boolean; team_ids: number[] }>(
      'quality.alert.stage',
      ['|', ['team_ids', '=', false], ['team_ids', 'in', [teamId]]],
      ['id', 'name', 'sequence', 'done', 'team_ids'],
      { order: 'sequence,id', limit: 50 },
    );
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      sequence: r.sequence ?? 0,
      done: !!r.done,
      teamIds: r.team_ids ?? [],
    }));
  },

  /** Move a quality alert to a stage — what dragging its card does in Odoo. */
  setAlertStage: (alertId: number, stageId: number) =>
    callKw<unknown>('quality.alert', 'write', [[alertId], { stage_id: stageId }]),

  /** Correct a quality alert's category pair. */
  setAlertCategory: (alertId: number, categoryId: number | null, subcategoryId: number) =>
    callKw<unknown>('quality.alert', 'write', [
      [alertId],
      { subcategory_id: subcategoryId, ...(categoryId != null ? { category_id: categoryId } : {}) },
    ]),

  /**
   * The chatter on a quality alert: what people have said on it.
   *
   * Comments and notes only. Odoo also logs every field change and every
   * stage move as a message, and a sheet showing "Stage: New → Assigned" three
   * times between two sentences reads as noise, not a conversation.
   */
  async alertMessages(alertId: number): Promise<AlertMessageVM[]> {
    const rows = await searchRead<{
      id: number; body: string | false; date: string; author_id: M2O; message_type: string;
    }>(
      'mail.message',
      [['model', '=', 'quality.alert'], ['res_id', '=', alertId], ['message_type', '=', 'comment']],
      ['id', 'body', 'date', 'author_id', 'message_type'],
      { order: 'id asc', limit: 80 },
    );
    return rows
      .map((r) => ({
        id: r.id,
        authorName: label(r.author_id),
        date: r.date,
        text: htmlToText(typeof r.body === 'string' ? r.body : ''),
      }))
      .filter((m) => m.text.length > 0);
  },

  /** Say something on a quality alert. An internal note, like every note this app posts. */
  postAlertMessage: (alertId: number, body: string) =>
    callKw<unknown>('quality.alert', 'message_post', [[alertId]], { body }),

  // ── downtime ─────────────────────────────────────────────────────────────
  /**
   * The open downtime record for a station, if it is blocked — with the reason,
   * because "this station is stopped" is not much use to someone standing at it
   * without saying why.
   *
   * A failure here is not swallowed. Treating an unreadable answer as "not
   * blocked" would quietly hide a stopped station, which is the one state the
   * operator most needs to see.
   */
  async openDowntime(workcentreId: number): Promise<{ id: number; reason: string } | null> {
    const rows = await searchRead<{ id: number; loss_id: M2O; description: string | false }>(
      'mrp.workcenter.productivity',
      [
        ['workcenter_id', '=', workcentreId],
        ['date_end', '=', false],
        ['loss_id.loss_type', '!=', 'productive'],
      ],
      ['id', 'loss_id', 'description'],
      { limit: 1 },
    );
    const row = rows[0];
    if (!row) return null;
    return { id: row.id, reason: row.description || label(row.loss_id) || 'Work centre blocked' };
  },

  // ── journey, completions, board stats ────────────────────────────────────
  async journey(moId: number): Promise<JourneyStepVM[]> {
    const rows = await searchRead<{
      id: number; name: string; workcenter_id: M2O; state: WoState; sequence: number;
      blocked_by_workorder_ids: number[];
    }>('mrp.workorder', [['production_id', '=', moId]],
      ['id', 'name', 'workcenter_id', 'state', 'sequence', 'blocked_by_workorder_ids'], {
      order: 'sequence,id',
      limit: 100,
    });
    const firstOpen = rows.findIndex((r) => r.state !== 'done' && r.state !== 'cancel');
    return rows.map((r, i) => ({
      woId: r.id,
      name: r.name,
      workcentreId: id(r.workcenter_id) ?? 0,
      workcentreName: label(r.workcenter_id),
      state: r.state,
      sequence: r.sequence ?? i,
      blockedBy: r.blocked_by_workorder_ids ?? [],
      status: r.state === 'done' ? 'done' : i === firstOpen ? 'current' : 'todo',
    }));
  },

  /**
   * What this station finished on a named day — today unless asked otherwise.
   *
   * Bounded at both ends rather than just `>= midnight`, because the moment the
   * day is a choice an open-ended window would answer "the 3rd" with every pod
   * finished since the 3rd. Today reads the same either way; a past day does
   * not.
   */
  async completedOn(
    workcentreId: number,
    flowlineId: number | null = null,
    day: string = localDateKey(),
  ): Promise<CompletedPodVM[]> {
    const domain: unknown[] = [
      ['workcenter_id', '=', workcentreId],
      ['state', '=', 'done'],
      ['date_finished', '>=', dayStartUtc(day)],
      ['date_finished', '<', dayStartUtc(shiftDateKey(day, 1))],
    ];
    // Scoped like the queue is, so "today's pods" answers for the flowlines the
    // operator is actually looking at rather than for the whole station.
    if (flowlineId) domain.push(['flowline_id', '=', flowlineId]);
    const wos = await searchRead<WoRow & { date_finished: string }>(
      'mrp.workorder',
      domain,
      [...WO_FIELDS, 'date_finished'],
      { order: 'date_finished desc', limit: 40 },
    );
    const pods = await this.hydrate(wos);
    const moIds = pods.map((p) => p.moId).filter(Boolean);
    const rft = new Map<number, boolean>();
    if (moIds.length) {
      const mos = await callKw<Array<{ id: number; is_rft: boolean }>>('mrp.production', 'read', [moIds], {
        fields: ['id', 'is_rft'],
      });
      for (const m of mos) rft.set(m.id, m.is_rft);
    }
    return pods.map((p, i) => ({
      woId: p.woId,
      mo: p.mo,
      proj: p.proj,
      type: p.type,
      // Odoo's datetimes are UTC; the floor reads its own clock.
      time: odooWallTime(wos[i]?.date_finished ?? ''),
      rft: rft.get(p.moId) ?? false,
      flowlineId: p.flowlineId,
      flowlineName: p.flowlineName,
      productionSeq: p.productionSeq,
    }));
  },

  // ── score, pace and station state ────────────────────────────────────────

  /**
   * Where every work centre stands on every flowline it serves.
   *
   * One read answers the scope chips, the starvation line, the priority order
   * and the tile colours, because Odoo's own reconciler has already worked all
   * of it out — see the allow-list entry for what each column means.
   *
   * Paged rather than capped: there are 60 work centres and 6 flowlines, and
   * the BFF reads at most 200 rows a call. A silent truncation here would drop
   * flowlines off the leader board with nothing to show it had happened.
   */
  async stationStates(workcentreId?: number | null): Promise<StationStateVM[]> {
    const domain: unknown[] = [['active', '=', true]];
    if (workcentreId != null) domain.push(['workcenter_id', '=', workcentreId]);
    const fields = [
      'id', 'workcenter_id', 'flowline_id', 'status', 'scheduled_state',
      'wo_count', 'ready_count', 'blocked_count', 'in_progress_count',
      'outbound_waiting', 'target_today', 'last_done_at', 'open_stop_alert_id',
    ];
    interface Row {
      id: number; workcenter_id: M2O; flowline_id: M2O; status: StationStateVM['status'];
      scheduled_state: string; wo_count: number; ready_count: number; blocked_count: number;
      in_progress_count: number; outbound_waiting: number; target_today: number;
      last_done_at: string | false; open_stop_alert_id: M2O;
    }
    const rows: Row[] = [];
    for (let offset = 0; offset < 600; offset += MAX_READ_LIMIT) {
      const page = await searchRead<Row>('mrp.workcenter.flowline.state', domain, fields, {
        order: 'flowline_id,workcenter_id',
        limit: MAX_READ_LIMIT,
        offset,
      });
      rows.push(...page);
      if (page.length < MAX_READ_LIMIT) break;
    }
    return rows.map((r) => ({
      id: r.id,
      workcentreId: id(r.workcenter_id) ?? 0,
      workcentreName: label(r.workcenter_id),
      flowlineId: id(r.flowline_id) ?? 0,
      flowlineName: label(r.flowline_id),
      status: r.status,
      onShift: r.scheduled_state === 'scheduled',
      waiting: r.wo_count ?? 0,
      ready: r.ready_count ?? 0,
      blocked: r.blocked_count ?? 0,
      running: r.in_progress_count ?? 0,
      outboundWaiting: r.outbound_waiting ?? 0,
      targetToday: r.target_today ?? 0,
      lastDoneAt: typeof r.last_done_at === 'string' ? r.last_done_at : null,
      stopAlert: Array.isArray(r.open_stop_alert_id)
        ? { id: r.open_stop_alert_id[0], name: r.open_stop_alert_id[1] }
        : null,
    }));
  },

  // ── the route: who is downstream of whom ─────────────────────────────────

  /**
   * The published routing graph, when there is one.
   *
   * `mrp.flowline.operation` is the only model that states the route as a
   * route. Until the process editor publishes into it the read comes back
   * empty, which is a fact the caller acts on rather than an error — see
   * `lib/topology.ts` for what it falls back to.
   */
  async flowlineOperations(): Promise<FlowlineOperationVM[]> {
    interface Row {
      id: number; name: string; flowline_id: M2O; workcenter_id: M2O; sequence: number;
      is_core: boolean; blocked_by_operation_ids: number[]; needed_by_operation_ids: number[];
      core_successor_id: M2O;
    }
    const rows: Row[] = [];
    if (topologyAbsent) return [];
    for (let offset = 0; offset < 1000; offset += MAX_READ_LIMIT) {
      let page: Row[];
      try {
        page = await searchRead<Row>(
        'mrp.flowline.operation',
        [['active', '=', true]],
        [
          'id', 'name', 'flowline_id', 'workcenter_id', 'sequence', 'is_core',
          'blocked_by_operation_ids', 'needed_by_operation_ids', 'core_successor_id',
        ],
        { order: 'flowline_id,sequence,id', limit: MAX_READ_LIMIT, offset },
      );
      } catch (e) {
        if (!absentHere(e)) throw e;
        topologyAbsent = true;
        return [];
      }
      rows.push(...page);
      if (page.length < MAX_READ_LIMIT) break;
    }
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      flowlineId: id(r.flowline_id) ?? 0,
      workcentreId: id(r.workcenter_id) ?? 0,
      workcentreName: label(r.workcenter_id),
      sequence: r.sequence ?? 0,
      core: !!r.is_core,
      blockedBy: r.blocked_by_operation_ids ?? [],
      blocks: r.needed_by_operation_ids ?? [],
      coreSuccessorId: id(r.core_successor_id),
    }));
  },

  /** The buffers between operations, and how full each one is right now. */
  async flowlineBuffers(): Promise<FlowlineBufferVM[]> {
    interface Row {
      id: number; name: string; flowline_id: M2O; from_operation_id: M2O; to_operation_id: M2O;
      level_min: number; level_target: number; level_max: number; filled: number;
      state: FlowlineBufferVM['state'];
    }
    if (topologyAbsent) return [];
    let rows: Row[];
    try {
      rows = await searchRead<Row>(
      'mrp.flowline.buffer',
      [],
      [
        'id', 'name', 'flowline_id', 'from_operation_id', 'to_operation_id',
        'level_min', 'level_target', 'level_max', 'filled', 'state',
      ],
      { order: 'flowline_id,id', limit: MAX_READ_LIMIT },
    );
    } catch (e) {
      if (!absentHere(e)) throw e;
      topologyAbsent = true;
      return [];
    }
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      flowlineId: id(r.flowline_id) ?? 0,
      fromOperationId: id(r.from_operation_id) ?? 0,
      toOperationId: id(r.to_operation_id) ?? 0,
      filled: r.filled ?? 0,
      min: r.level_min ?? 0,
      target: r.level_target ?? 0,
      max: r.level_max ?? 0,
      state: r.state,
    }));
  },

  /**
   * The route as the pods in front of the operator are actually walking it.
   *
   * Odoo's work orders carry `blocked_by_workorder_ids`, and on this instance
   * they are populated: "Stand Pod" at Pod Standing - Area C is blocked by CNC
   * and Astron Tile QC, and blocks the eight flowline stations. So a handful of
   * live pods state the route exactly, per pod type, with no configuration at
   * all — which is what makes this a fallback worth having rather than a guess.
   *
   * One read for a few MOs, not the whole plan: the lead pod on each line at
   * this station is enough to name that line's next station.
   */
  async podRouting(moIds: number[]): Promise<RouteEdgeVM[]> {
    if (moIds.length === 0) return [];
    interface Row {
      id: number; production_id: M2O; workcenter_id: M2O; flowline_id: M2O;
      sequence: number; state: WoState; blocked_by_workorder_ids: number[];
    }
    const rows: Row[] = [];
    for (let offset = 0; offset < 1000; offset += MAX_READ_LIMIT) {
      const page = await searchRead<Row>(
        'mrp.workorder',
        [['production_id', 'in', moIds]],
        ['id', 'production_id', 'workcenter_id', 'flowline_id', 'sequence', 'state', 'blocked_by_workorder_ids'],
        { order: 'production_id,sequence,id', limit: MAX_READ_LIMIT, offset },
      );
      rows.push(...page);
      if (page.length < MAX_READ_LIMIT) break;
    }
    return edgesFromSteps(
      rows.map((r) => ({
        woId: r.id,
        moId: id(r.production_id) ?? 0,
        flowlineId: id(r.flowline_id) ?? 0,
        workcentreId: id(r.workcenter_id) ?? 0,
        workcentreName: label(r.workcenter_id),
        sequence: r.sequence ?? 0,
        cancelled: r.state === 'cancel',
        blockedBy: r.blocked_by_workorder_ids ?? [],
      })),
    );
  },

  /**
   * A few live pods per flowline, to read the route off.
   *
   * Two per line, not two hundred: one pod's work orders carry that line's
   * whole chain, and a second covers the common case of two pod types running
   * the same line through different stations (Tiled waterproofs at one work
   * centre, Astron at another). The operator's own queue is sampled alongside
   * this, so the variant actually in front of them is always among them.
   */
  async routeSampleMos(flowlineIds: number[], perLine = 2): Promise<number[]> {
    const found = await Promise.all(
      flowlineIds.map((flowlineId) =>
        searchRead<{ id: number; production_id: M2O }>(
          'mrp.workorder',
          [['flowline_id', '=', flowlineId], ['on_hold', '=', false], ...QUEUE_DOMAIN],
          ['id', 'production_id'],
          { order: QUEUE_ORDER, limit: perLine },
        ),
      ),
    );
    const ids = new Set<number>();
    for (const rows of found) for (const r of rows) {
      const moId = id(r.production_id);
      if (moId != null) ids.add(moId);
    }
    return [...ids];
  },

  /**
   * Today's target for each flowline, and the per-station overrides of it.
   *
   * The flowline number is the master plan: every station on a line inherits
   * it, and only a station deliberately running to a different figure today
   * gets a row of its own. So this returns both and the caller resolves
   * override-then-inherit, which is `targetFor` below.
   */
  async targets(date?: string): Promise<{
    flowline: Record<number, { target: number; name: string }>;
    workcentre: Record<string, number>;
  }> {
    const day = date ?? localDateKey();
    const [lineRows, wcRows] = await Promise.all([
      searchRead<{ id: number; flowline_id: M2O; target: number }>(
        'mrp.flowline.target',
        [['date', '=', day]],
        ['id', 'flowline_id', 'target'],
        { limit: 50 },
      ),
      searchRead<{ id: number; workcenter_id: M2O; flowline_id: M2O; target: number }>(
        'mrp.workcenter.target',
        [['date', '=', day]],
        ['id', 'workcenter_id', 'flowline_id', 'target'],
        { limit: MAX_READ_LIMIT },
      ),
    ]);
    const flowline: Record<number, { target: number; name: string }> = {};
    for (const r of lineRows) {
      const flId = id(r.flowline_id);
      if (flId != null) flowline[flId] = { target: r.target ?? 0, name: label(r.flowline_id) };
    }
    const workcentre: Record<string, number> = {};
    for (const r of wcRows) {
      const wcId = id(r.workcenter_id);
      const flId = id(r.flowline_id);
      if (wcId != null && flId != null) workcentre[`${wcId}:${flId}`] = r.target ?? 0;
    }
    return { flowline, workcentre };
  },

  /**
   * Completions today, grouped. `read_group` rather than a row read: a
   * department's worth of finished work orders would blow the read limit, and
   * the screens only ever want the count.
   */
  async doneToday(opts: {
    workcentreId?: number | null;
    byHour?: boolean;
  } = {}): Promise<Array<{ workcentreId: number; flowlineId: number; hour: number | null; count: number }>> {
    const domain: unknown[] = [
      ['state', '=', 'done'],
      ['date_finished', '>=', localMidnightUtc()],
    ];
    if (opts.workcentreId != null) domain.push(['workcenter_id', '=', opts.workcentreId]);
    const groupby = ['workcenter_id', 'flowline_id', ...(opts.byHour ? ['date_finished:hour'] : [])];
    const groups = await callKw<Array<Record<string, unknown>>>('mrp.workorder', 'read_group', [], {
      domain,
      fields: ['id:count'],
      groupby,
      lazy: false,
      limit: MAX_READ_LIMIT,
    });
    return groups.map((g) => ({
      workcentreId: id(g['workcenter_id'] as M2O) ?? 0,
      flowlineId: id(g['flowline_id'] as M2O) ?? 0,
      hour: opts.byHour ? hourOf(g['date_finished:hour']) : null,
      count: groupCount(g, groupby),
    }));
  },

  /**
   * Shift calendars, flattened to productive spans.
   *
   * Plural because the leader board needs one per work centre and the floor
   * runs three between sixty stations — asking once for the set is two reads
   * where asking per station would be a hundred and twenty.
   */
  async calendars(calendarIds: number[]): Promise<Record<number, CalendarVM>> {
    const ids = [...new Set(calendarIds)].filter((v) => Number.isInteger(v) && v > 0);
    if (ids.length === 0) return {};
    const [cals, rows] = await Promise.all([
      callKw<Array<{ id: number; name: string; tz: string | false; two_weeks_calendar: boolean }>>(
        'resource.calendar',
        'read',
        [ids],
        { fields: ['id', 'name', 'tz', 'two_weeks_calendar'] },
      ),
      searchRead<{
        id: number; calendar_id: M2O; dayofweek: string; hour_from: number; hour_to: number;
        day_period: string; week_type: string | false;
      }>(
        'resource.calendar.attendance',
        [['calendar_id', 'in', ids]],
        ['id', 'calendar_id', 'dayofweek', 'hour_from', 'hour_to', 'day_period', 'week_type'],
        { limit: MAX_READ_LIMIT },
      ),
    ]);

    const out: Record<number, CalendarVM> = {};
    for (const cal of cals) {
      out[cal.id] = {
        id: cal.id,
        name: cal.name,
        tz: typeof cal.tz === 'string' && cal.tz ? cal.tz : 'Australia/Sydney',
        twoWeeks: !!cal.two_weeks_calendar,
        spans: [],
      };
    }
    for (const r of rows) {
      // Odoo models tea and lunch as attendance rows of their own. They are
      // scheduled, but they are not work, and counting them would tell a
      // station it was behind for the half hour it was having lunch.
      if (r.day_period === 'lunch') continue;
      const cal = out[id(r.calendar_id) ?? -1];
      if (!cal) continue;
      cal.spans.push({
        dayofweek: Number(r.dayofweek),
        from: r.hour_from,
        to: r.hour_to,
        weekType: typeof r.week_type === 'string' && r.week_type ? r.week_type : null,
      });
    }
    return out;
  },

  // ── andons and todos ─────────────────────────────────────────────────────

  /**
   * Andons, read only.
   *
   * This app shows them and links out; acknowledging happens in the Andon app,
   * which owns the clock and the escalation ladder behind it.
   */
  async andons(scope: {
    employeeId?: number | null;
    workcentreId?: number | null;
    ownerUid?: number | null;
    unacknowledged?: boolean;
    /** Named alerts, open or not — for a sheet opened on one. */
    ids?: number[];
    limit?: number;
  }): Promise<AndonVM[]> {
    const domain: unknown[] = scope.ids ? [['id', 'in', scope.ids]] : [['is_open', '=', true]];
    if (scope.unacknowledged) domain.push(['state', '=', 'new']);
    if (scope.workcentreId != null) domain.push(['workcenter_id', '=', scope.workcentreId]);
    if (scope.employeeId != null) domain.push(['employee_id', '=', scope.employeeId]);
    if (scope.ownerUid != null) domain.push(['owner_id', '=', scope.ownerUid]);

    const rows = await searchRead<{
      id: number; name: string; title: string; state: AndonState; stars: string;
      workcenter_id: M2O; flowline_id: M2O; production_id: M2O; production_name: string | false;
      employee_id: M2O; employee_name: string | false; owner_name: string | false;
      raised_at: string; acknowledge_overdue: boolean; sla_breached: boolean;
      eta_minutes: number; responding_at: string | false;
      operator_words: string | false; alert_type_key: string | false;
    }>('andon.alert', domain, [
      'id', 'name', 'title', 'state', 'stars', 'workcenter_id', 'flowline_id', 'production_id',
      'production_name', 'employee_id', 'employee_name', 'owner_name', 'raised_at',
      'acknowledge_overdue', 'sla_breached', 'eta_minutes', 'responding_at',
      'operator_words', 'alert_type_key',
    ], { order: 'stars desc, raised_at desc', limit: scope.limit ?? 25 });

    const now = Date.now();
    return rows.map((r) => ({
      id: r.id,
      ref: r.name,
      title: r.title,
      state: r.state,
      stars: (Number(r.stars) || 1) as 1 | 2 | 3,
      workcentreId: id(r.workcenter_id),
      workcentreName: label(r.workcenter_id),
      flowlineId: id(r.flowline_id),
      flowlineName: label(r.flowline_id),
      moId: id(r.production_id),
      mo: label(r.production_id) || (typeof r.production_name === 'string' ? r.production_name : ''),
      raisedByEmployeeId: id(r.employee_id),
      raisedByName: label(r.employee_id) || (typeof r.employee_name === 'string' ? r.employee_name : ''),
      ownerName: typeof r.owner_name === 'string' ? r.owner_name : '',
      raisedAt: r.raised_at,
      ageMinutes: minutesSince(r.raised_at, now),
      overdue: !!r.acknowledge_overdue,
      slaBreached: !!r.sla_breached,
      etaMinutes: r.eta_minutes || null,
      respondingAt: typeof r.responding_at === 'string' ? r.responding_at : null,
      words: typeof r.operator_words === 'string' ? r.operator_words.trim() : '',
      typeKey: typeof r.alert_type_key === 'string' ? r.alert_type_key : '',
    }));
  },

  /** Every open andon on the floor, counted. */
  andonOpenCount: () =>
    callKw<number>('andon.alert', 'search_count', [], { domain: [['is_open', '=', true]] }),

  /** One andon, open or not, for the detail sheet. */
  async andonById(andonId: number): Promise<AndonVM | null> {
    const rows = await api.andons({ ids: [andonId], limit: 1 });
    return rows[0] ?? null;
  },

  // ── buffers, as the floor runs them ──────────────────────────────────────

  /**
   * Every buffer on every flowline, with its levels and how full it is.
   *
   * `mrp.buffer.flowline`, the kiosk buffer boards' own table — fifty-odd live
   * rows on this instance, against the zero the process editor's
   * `mrp.flowline.buffer` holds. The buffer's name comes from its parent row;
   * the flowline row's own name is the three endpoints glued together.
   */
  async bufferLines(): Promise<BufferLineVM[]> {
    interface Row {
      id: number; buffer_id: M2O; flowline_id: M2O; input_workcenter_id: M2O; output_workcenter_id: M2O;
      level_critical: number; level_minimum: number; level_maximum: number;
      state: BufferLineVM['state']; filled_slots: number; in_sequence_count: number; in_sequence: boolean;
    }
    const rows: Row[] = [];
    for (let offset = 0; offset < 1000; offset += MAX_READ_LIMIT) {
      const page = await searchRead<Row>(
        'mrp.buffer.flowline',
        [],
        [
          'id', 'buffer_id', 'flowline_id', 'input_workcenter_id', 'output_workcenter_id',
          'level_critical', 'level_minimum', 'level_maximum', 'state', 'filled_slots',
          'in_sequence_count', 'in_sequence',
        ],
        { order: 'buffer_id,flowline_id,id', limit: MAX_READ_LIMIT, offset },
      );
      rows.push(...page);
      if (page.length < MAX_READ_LIMIT) break;
    }
    // The buffer's display name is its feeding work centre unless somebody
    // renamed it on the board, which they have — "Steel Cutting > Welding".
    const bufferIds = [...new Set(rows.map((r) => id(r.buffer_id)).filter((v): v is number => v != null))];
    const names = new Map<number, string>();
    if (bufferIds.length) {
      const defs = await callKw<Array<{ id: number; name: string | false }>>('mrp.buffer', 'read', [bufferIds], {
        fields: ['id', 'name'],
      });
      for (const d of defs) names.set(d.id, typeof d.name === 'string' ? d.name : '');
    }
    return rows.map((r) => {
      const bufferId = id(r.buffer_id);
      return {
        id: r.id,
        bufferId,
        bufferName:
          (bufferId != null && names.get(bufferId)) ||
          label(r.buffer_id) ||
          `${label(r.input_workcenter_id)} > ${label(r.output_workcenter_id)}`,
        flowlineId: id(r.flowline_id) ?? 0,
        flowlineName: label(r.flowline_id),
        inWorkcentreId: id(r.input_workcenter_id),
        inWorkcentreName: label(r.input_workcenter_id),
        outWorkcentreId: id(r.output_workcenter_id),
        outWorkcentreName: label(r.output_workcenter_id),
        critical: r.level_critical ?? 0,
        min: r.level_minimum ?? 0,
        max: r.level_maximum ?? 0,
        state: r.state,
        filled: r.filled_slots ?? 0,
        inSequenceCount: r.in_sequence_count ?? 0,
        inSequence: !!r.in_sequence,
      };
    });
  },

  /**
   * Today's shifts, from attendance: who is on now and who has finished.
   *
   * Attendance rather than pod timers. Timers answer "who touched a pod", and
   * read as a shift they are wrong in both directions — a trolley run logs a
   * hundred and ninety of them, someone between pods has none open, and a
   * night shift that crossed midnight looked like it started at midnight.
   *
   * "Today" is any shift that touched the local day: started since midnight,
   * or finished since midnight (the night shift that started last evening), or
   * still open. Open shifts are bounded to the last sixteen hours because Tanda
   * leaves a missed clock-out open for up to a month, and a shift that started
   * last Tuesday is not somebody on the floor now.
   */
  // No arguments: this is handed to a query as a bare function, and React Query
  // passes its own context object as the first one.
  async crewToday(): Promise<CrewShiftVM[]> {
    const midnight = localMidnightUtc();
    const openSince = new Date(Date.now() - 16 * 3600_000).toISOString().slice(0, 19).replace('T', ' ');
    const domain: unknown[] = [
      '|', '|',
      ['check_in', '>=', midnight],
      ['check_out', '>=', midnight],
      '&', ['check_out', '=', false], ['check_in', '>=', openSince],
    ];
    type Row = {
      id: number; employee_id: M2O; check_in: string; check_out: string | false;
      worked_hours: number | false; worked_department_id: M2O;
    };
    const rows: Row[] = [];
    // A day is a hundred-odd shifts; the BFF reads two hundred a call.
    for (let offset = 0; offset < 1000; offset += MAX_READ_LIMIT) {
      const page = await searchRead<Row>(
        'hr.attendance',
        domain,
        ['id', 'employee_id', 'check_in', 'check_out', 'worked_hours', 'worked_department_id'],
        { order: 'check_in asc, id asc', limit: MAX_READ_LIMIT, offset },
      );
      rows.push(...page);
      if (page.length < MAX_READ_LIMIT) break;
    }
    return rows.flatMap((r) => {
      const empId = id(r.employee_id);
      if (empId == null) return [];
      const open = r.check_out === false;
      return [{
        attendanceId: r.id,
        employeeId: empId,
        name: label(r.employee_id),
        department: label(r.worked_department_id),
        checkIn: r.check_in,
        checkOut: open ? null : (r.check_out as string),
        workedHours: open || typeof r.worked_hours !== 'number' ? null : r.worked_hours,
        open,
      }];
    });
  },

  /**
   * Todos for one Odoo user.
   *
   * Odoo hangs activities off res.users and most people on the floor have no
   * user, so this is empty for most operators. That is an honest empty: the
   * caller only asks once it has a user id to ask about.
   */
  async todos(userId: number, limit = 25): Promise<TodoVM[]> {
    const rows = await searchRead<{
      id: number; res_model: string; res_id: number; res_name: string | false;
      summary: string | false; activity_type_id: M2O; date_deadline: string | false; state: string;
    }>(
      'mail.activity',
      [['user_id', '=', userId]],
      ['id', 'res_model', 'res_id', 'res_name', 'summary', 'activity_type_id', 'date_deadline', 'state'],
      { order: 'date_deadline asc, id asc', limit },
    );
    return rows.map((r) => ({
      id: r.id,
      summary: (typeof r.summary === 'string' && r.summary) || label(r.activity_type_id) || 'To-do',
      kind: label(r.activity_type_id),
      about: typeof r.res_name === 'string' ? r.res_name : '',
      resModel: r.res_model,
      resId: r.res_id,
      deadline: typeof r.date_deadline === 'string' ? r.date_deadline : null,
      state: r.state,
    }));
  },

  /** The Odoo user behind an employee, when they have one. */
  async userIdForEmployee(employeeId: number): Promise<number | null> {
    for (const model of ['hr.employee', 'hr.employee.public'] as const) {
      try {
        const [row] = await callKw<Array<{ id: number; user_id: M2O }>>(model, 'read', [[employeeId]], {
          fields: ['id', 'user_id'],
        });
        return id(row?.user_id ?? false);
      } catch {
        // Try the public twin, exactly as the roster read does.
      }
    }
    return null;
  },

  /** Leaf departments, for the leader's filter chips. */
  async departments(): Promise<DepartmentVM[]> {
    const rows = await searchRead<{
      id: number; name: string; complete_name: string; parent_id: M2O; manager_id: M2O;
    }>('hr.department', [['active', '=', true]], ['id', 'name', 'complete_name', 'parent_id', 'manager_id'], {
      limit: 100,
    });
    return rows.map((r) => ({
      id: r.id,
      name: r.name,
      completeName: r.complete_name,
      parentId: id(r.parent_id),
      managerId: id(r.manager_id),
    }));
  },

  /**
   * Everyone on the clock across the floor, for the leader board's head count.
   *
   * The same open-productivity-line reading `crewAtStation` uses, without the
   * work-centre filter — an aggregate and a few faces, deliberately not a
   * per-person table. Who is where is a management question the floor does not
   * need a wall board asking in public.
   */
  async crewOnFloor(): Promise<{ id: number; name: string }[]> {
    const lines = await searchRead<{ id: number; employee_id: M2O }>(
      'mrp.workcenter.productivity',
      [
        ['date_end', '=', false],
        ['employee_id', '!=', false],
        ['loss_id.loss_type', '=', 'productive'],
      ],
      ['id', 'employee_id'],
      { limit: MAX_READ_LIMIT },
    );
    const seen = new Map<number, string>();
    for (const line of lines) {
      const empId = id(line.employee_id);
      if (empId != null && !seen.has(empId)) seen.set(empId, label(line.employee_id));
    }
    return [...seen].map(([empId, name]) => ({ id: empId, name }));
  },
};

/** Minutes since an Odoo datetime, which is naive UTC. */
function minutesSince(odooDatetime: string, now: number): number {
  const then = Date.parse(odooDatetime.replace(' ', 'T') + 'Z');
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.round((now - then) / 60000));
}

/**
 * The hour out of a `date_finished:hour` group label.
 *
 * Odoo renders it for people rather than for parsing — "30 Aug 2026, 2:00 PM"
 * or "14:00" depending on the user's language — so this takes the first
 * hour-looking number it can find and reads the meridiem beside it.
 */
function hourOf(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const m = /(\d{1,2}):(\d{2})\s*(am|pm)?/i.exec(value);
  if (!m) return null;
  let hour = Number(m[1]);
  const meridiem = m[3]?.toLowerCase();
  if (meridiem === 'pm' && hour < 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;
  return hour >= 0 && hour <= 23 ? hour : null;
}

/**
 * The count out of a read_group row.
 *
 * Odoo names it `__count` for a non-lazy grouping and `<field>_count` for a
 * lazy one, and has moved between the two across versions. Asking for each in
 * turn is cheaper than pinning a version.
 */
function groupCount(group: Record<string, unknown>, groupby: string[]): number {
  const candidates = ['__count', 'id_count', ...groupby.map((g) => `${g.split(':')[0]}_count`)];
  for (const key of candidates) {
    const v = group[key];
    if (typeof v === 'number') return v;
  }
  return 0;
}


export type ShopFloorApi = typeof api;
